import { Controller } from "@hotwired/stimulus"
import * as d3 from "d3"

export default class extends Controller {
  static values = {
    data: Array,
    width: Number,
    height: Number
  }

  connect() {
    this.initializeChart()
  }

  initializeChart() {
    // Add position relative to container
    d3.select(this.element)
      .style("position", "relative")

    const width = this.widthValue || 600
    const height = this.heightValue || 400

    // Create tooltip div with updated styles
    const tooltip = d3.select(this.element)
      .append("div")
      .attr("class", "absolute hidden bg-gray-800 text-white px-3 py-2 rounded text-sm shadow-lg z-10")
      .style("pointer-events", "none")
      .style("position", "absolute")

    const svg = d3.select(this.element)
      .append("svg")
      .attr("width", width)
      .attr("height", height)

    // Add gradient definition
    const gradient = svg.append("defs")
      .append("linearGradient")
      .attr("id", "area-gradient")
      .attr("gradientUnits", "userSpaceOnUse")
      .attr("x1", 0)
      .attr("y1", height)
      .attr("x2", 0)
      .attr("y2", 0)

    gradient.append("stop")
      .attr("offset", "0%")
      .attr("stop-color", "rgb(59, 130, 246)")
      .attr("stop-opacity", 0.1)

    gradient.append("stop")
      .attr("offset", "100%")
      .attr("stop-color", "rgb(59, 130, 246)")
      .attr("stop-opacity", 0.4)

    const x = d3.scaleTime()
      .range([0, width])
      .domain(d3.extent(this.dataValue, d => new Date(d.date)))

    const y = d3.scaleLinear()
      .range([height, 0])
      .domain([0, d3.max(this.dataValue, d => d.value)])

    const area = d3.area()
      .x(d => x(new Date(d.date)))
      .y0(height) // Start from bottom
      .y1(height) // Start from bottom for animation
      .curve(d3.curveMonotoneX)

    const areaWithData = d3.area()
      .x(d => x(new Date(d.date)))
      .y0(height)
      .y1(d => y(d.value))
      .curve(d3.curveMonotoneX)

    const line = d3.line()
      .x(d => x(new Date(d.date)))
      .y(height) // Start from bottom for animation
      .curve(d3.curveMonotoneX)

    const lineWithData = d3.line()
      .x(d => x(new Date(d.date)))
      .y(d => y(d.value))
      .curve(d3.curveMonotoneX)

    // Add the area with transition
    svg.append("path")
      .datum(this.dataValue)
      .attr("fill", "url(#area-gradient)")
      .attr("d", area)
      .transition()
      .duration(1000)
      .ease(d3.easeQuadOut)
      .attr("d", areaWithData)

    // Add the line with transition
    const path = svg.append("path")
      .datum(this.dataValue)
      .attr("fill", "none")
      .attr("stroke", "rgb(59, 130, 246)")
      .attr("stroke-width", 2)
      .attr("d", line)
      .transition()
      .duration(1000)
      .ease(d3.easeQuadOut)
      .attr("d", lineWithData)

    // Add dots with transition
    const dots = svg.selectAll(".dot")
      .data(this.dataValue)
      .enter()
      .append("circle")
      .attr("class", "dot")
      .attr("cx", d => x(new Date(d.date)))
      .attr("cy", height) // Start from bottom
      .attr("r", 8)
      .attr("fill", "transparent")
      .on("mouseover", (event, d) => {
        // Get element's position relative to the container
        const rect = this.element.getBoundingClientRect()
        const xPos = x(new Date(d.date))
        const yPos = y(d.value)
        
        tooltip
          .html(`
            <div class="font-medium">${new Date(d.date).toLocaleDateString()}</div>
            <div>Value: ${d.value}</div>
          `)
          .classed("hidden", false)
          .style("left", `${xPos + 10}px`)
          .style("top", `${yPos - 40}px`)
      })
      .on("mouseout", () => {
        tooltip.classed("hidden", true)
      })

    // Transition dots to their actual positions
    dots.transition()
      .duration(1000)
      .delay((d, i) => i * 10) // Slight delay for each dot
      .ease(d3.easeQuadOut)
      .attr("cy", d => y(d.value))

    // Add visible dots with transition
    svg.selectAll(".visible-dot")
      .data(this.dataValue)
      .enter()
      .append("circle")
      .attr("class", "visible-dot")
      .attr("cx", d => x(new Date(d.date)))
      .attr("cy", height) // Start from bottom
      .attr("r", 3)
      .attr("fill", "rgb(59, 130, 246)")
      .transition()
      .duration(1000)
      .delay((d, i) => i * 10) // Slight delay for each dot
      .ease(d3.easeQuadOut)
      .attr("cy", d => y(d.value))
  }
}
